import React, { useState, useEffect } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { FiPackage, FiUser, FiSettings, FiBriefcase, FiCheckCircle, FiClock, FiPlus } from 'react-icons/fi';
import API from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './DashboardPage.css';
const statusColors = {
  pending: '#f59e0b', confirmed: '#3b82f6', processing: '#8b5cf6',
  printing: '#ec4899', shipped: '#06b6d4', delivered: '#059669',
  cancelled: '#ef4444', refunded: '#6b7280',
};

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get('/orders/my').then(r => setOrders(r.data.orders || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="spinner"/>;
  if (!orders.length) return (
    <div style={{textAlign:'center',padding:'60px 0'}}>
      <div style={{fontSize:'3rem',marginBottom:16}}>📦</div>
      <h3>No orders yet</h3>
      <p style={{color:'var(--text-muted)',marginBottom:20}}>Start shopping to see your orders here.</p>
      <Link to="/products" className="btn btn-primary">Shop Now</Link>
    </div>
  );

  return (
    <div>
      <h2 style={{marginBottom:24}}>My Orders</h2>
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        {orders.map(o => (
          <div key={o.id} style={{background:'#fff',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:24}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12}}>
              <div>
                <div style={{fontWeight:700,fontSize:'1rem',marginBottom:4}}>Order #{o.orderNumber}</div>
                <div style={{fontSize:'0.82rem',color:'var(--text-muted)'}}>{new Date(o.createdAt).toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'})}</div>
              </div>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <span style={{padding:'4px 12px',borderRadius:100,fontSize:'0.78rem',fontWeight:600,background:`${statusColors[o.status]}20`,color:statusColors[o.status]}}>
                  {o.status.charAt(0).toUpperCase()+o.status.slice(1)}
                </span>
                <span style={{fontWeight:800,fontSize:'1.05rem',color:'var(--primary)',fontFamily:'var(--font-display)'}}>
                  ₹{parseFloat(o.totalAmount).toFixed(2)}
                </span>
              </div>
            </div>
            <div style={{marginTop:16,display:'flex',flexWrap:'wrap',gap:12}}>
              {(o.items || []).slice(0,3).map((item,i) => (
                <div key={i} style={{background:'var(--bg-soft)',padding:'6px 12px',borderRadius:8,fontSize:'0.82rem'}}>
                  {item.name} × {item.quantity}
                </div>
              ))}
              {o.items?.length > 3 && <div style={{background:'var(--bg-soft)',padding:'6px 12px',borderRadius:8,fontSize:'0.82rem',color:'var(--text-muted)'}}>+{o.items.length-3} more</div>}
            </div>
            {o.trackingNumber && (
              <div style={{marginTop:12,padding:12,background:'#f0fdf4',borderRadius:8,fontSize:'0.82rem',color:'#059669'}}>
                📦 Tracking: <strong>{o.trackingNumber}</strong>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── My Business Tab ──────────────────────────────────────────────────────────
const Row = ({ label, value }) => value ? (
  <div style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid var(--border)',fontSize:'0.85rem',gap:12}}>
    <span style={{color:'var(--text-muted)',flexShrink:0}}>{label}</span>
    <strong style={{textAlign:'right'}}>{value}</strong>
  </div>
) : null;

const Section = ({ title, children }) => (
  <div style={{background:'var(--bg-soft)',border:'1px solid var(--border)',borderRadius:'var(--radius)',padding:20,marginBottom:16}}>
    <h4 style={{fontSize:'0.9rem',fontWeight:700,marginBottom:14}}>{title}</h4>
    {children}
  </div>
);

// One business's full details, shown when that business is expanded
const BusinessCard = ({ business, expanded, onToggle, navigate }) => {
  const isVerified = business.isVerified;
  const statusBadge = isVerified
    ? { icon: <FiCheckCircle size={16}/>, label: 'Verified', bg: '#f0fdf4', color: '#059669', border: '#bbf7d0' }
    : { icon: <FiClock size={16}/>, label: 'Pending Approval', bg: '#fffbeb', color: '#d97706', border: '#fde68a' };

  return (
    <div style={{border:'1px solid var(--border)',borderRadius:'var(--radius)',marginBottom:16,overflow:'hidden'}}>
      <div
        style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'16px 20px',gap:12,flexWrap:'wrap',cursor:'pointer'}}
        onClick={onToggle}
      >
        <div>
          <h3 style={{margin:0,fontSize:'1.05rem'}}>{business.companyName}</h3>
          <span style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>
            {business.companyType?.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}
          </span>
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <span style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',borderRadius:100,fontSize:'0.82rem',fontWeight:600,background:statusBadge.bg,color:statusBadge.color,border:`1px solid ${statusBadge.border}`}}>
            {statusBadge.icon} {statusBadge.label}
          </span>
        </div>
      </div>

      {expanded && (
        <div style={{padding:'0 20px 20px'}}>
          {!isVerified && (
            <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:'var(--radius)',padding:'14px 18px',marginBottom:20,fontSize:'0.85rem',color:'#92400e',display:'flex',gap:10,alignItems:'center'}}>
              <FiClock size={16} style={{flexShrink:0}}/>
              This business profile is under review. Our team will verify it within 24 hours.
            </div>
          )}

          <Section title="🏢 Company Information">
            <Row label="Industry" value={business.industry}/>
            <Row label="Website" value={business.website}/>
          </Section>

          <Section title="📋 Documents">
            <Row label="GST Number" value={business.gstNumber}/>
            <Row label="PAN Number" value={business.panNumber}/>
            <Row label="TAN Number" value={business.tanNumber}/>
            <Row label="Udyam Number" value={business.udyamNumber}/>
          </Section>

          <Section title="👤 Contact Person">
            <Row label="Name" value={business.contactPersonName}/>
            <Row label="Designation" value={business.contactPersonDesignation}/>
            <Row label="Phone" value={business.contactPersonPhone}/>
            <Row label="Email" value={business.contactPersonEmail}/>
          </Section>

          <Section title="📍 Registered Address">
            <Row label="Address" value={`${business.regAddressLine1}${business.regAddressLine2 ? ', '+business.regAddressLine2 : ''}`}/>
            <Row label="City" value={business.regCity}/>
            <Row label="State" value={business.regState}/>
            <Row label="Pincode" value={business.regPincode}/>
          </Section>

          {business.commAddressLine1 && (
            <Section title="📮 Communication Address">
              <Row label="Address" value={`${business.commAddressLine1}${business.commAddressLine2 ? ', '+business.commAddressLine2 : ''}`}/>
              <Row label="City" value={business.commCity}/>
              <Row label="State" value={business.commState}/>
              <Row label="Pincode" value={business.commPincode}/>
            </Section>
          )}

          {business.bankName && (
            <Section title="🏦 Bank Details">
              <Row label="Bank Name" value={business.bankName}/>
              <Row label="Account Number" value={business.accountNumber ? '••••'+business.accountNumber.slice(-4) : null}/>
              <Row label="Branch" value={business.branchName}/>
              <Row label="IFSC Code" value={business.ifscCode}/>
            </Section>
          )}
        </div>
      )}
    </div>
  );
};

const MyBusiness = () => {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    // Claim any unlinked businesses submitted via /business/new-register that
    // match this user's email + phone, then load the full list.
    API.post('/business/claim')
      .catch(() => {}) // non-fatal — proceed even if claim fails
      .finally(() => {
        API.get('/business/my')
          .then(r => { if (r.data.success) setBusinesses(r.data.businesses); })
          .catch(() => {})
          .finally(() => setLoading(false));
      });
  }, []);

  if (loading) return <div className="spinner"/>;

  if (businesses.length === 0) return (
    <div style={{textAlign:'center',padding:'60px 0'}}>
      <div style={{fontSize:'3rem',marginBottom:16}}>🏢</div>
      <h3>No Business Profile</h3>
      <p style={{color:'var(--text-muted)',marginBottom:20}}>You haven't registered a business yet.</p>
      <button className="btn btn-primary" onClick={() => navigate('/business/register')}>Register Business</button>
    </div>
  );

  return (
    <div>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24,flexWrap:'wrap',gap:12}}>
        <h2 style={{margin:0}}>My Businesses</h2>
        <button
          className="btn btn-primary"
          style={{fontSize:'0.85rem',padding:'8px 18px',display:'flex',alignItems:'center',gap:6}}
          onClick={() => navigate('/business/register')}
        >
          <FiPlus size={16}/> Add New Business
        </button>
      </div>

      {businesses.map(b => (
        <BusinessCard
          key={b.id}
          business={b}
          expanded={expandedId === b.id}
          onToggle={() => setExpandedId(curr => curr === b.id ? null : b.id)}
          navigate={navigate}
        />
      ))}
    </div>
  );
};

// ── Profile Tab ──────────────────────────────────────────────────────────────
const Profile = () => {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', address: user?.address || '' });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '' });
  const [saving, setSaving] = useState(false);
  const [otpStep, setOtpStep] = useState(false);   // ← is OTP input showing?
  const [otp, setOtp] = useState('');
  

  const phoneChanged = form.phone && form.phone !== user?.phone;

  // Step 1 — Send OTP if phone changed, else save directly
  const handleProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!form.phone && user?.phone) {
  toast.error('Phone number cannot be removed.');
  setSaving(false);
  return;
}
      if (phoneChanged) {
        // Send OTP first
        
        await API.post('/auth/send-otp', { phone: form.phone });
        setOtpStep(true);
        toast.success('OTP sent to your WhatsApp!');
      } else {
        // No phone change — save directly
        const { data } = await API.put('/auth/profile', form);
        updateUser(data.user);
        toast.success('Profile updated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    } finally {
      setSaving(false);
      
    }
  };

  // Step 2 — Verify OTP then save profile
  const handleOtpVerify = async () => {
    if (!otp) return toast.error('Please enter the OTP.');
    setSaving(true);
    try {
      // Verify OTP
      await API.post('/auth/verify-otp', { phone: form.phone, code: otp });
      // Now save profile — backend will find phone verified
      const { data } = await API.put('/auth/profile', form);
      updateUser(data.user);
      toast.success('Phone verified and profile updated!');
      setOtpStep(false);
      setOtp('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP.');
    } finally {
      setSaving(false);
    }
  };

  const handlePassword = async (e) => {
    e.preventDefault();
    try {
      await API.put('/auth/change-password', pwForm);
      toast.success('Password changed!');
      setPwForm({ currentPassword: '', newPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed.');
    }
  };

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Profile Settings</h2>
      {!user?.phone && (
  <div style={{
    background: '#fffbeb',
    border: '1px solid #fde68a',
    borderRadius: 'var(--radius)',
    padding: '14px 18px',
    marginBottom: 24,
    fontSize: '0.85rem',
    color: '#92400e',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span>⚠️</span>
      <div>
        <strong>Add your phone number</strong> to link any business profiles you've registered with us.
      </div>
    </div>
    <button
      className="btn btn-primary"
      style={{ fontSize: '0.8rem', padding: '6px 14px', whiteSpace: 'nowrap' }}
      onClick={() => document.querySelector('input[type="tel"]')?.focus()}
    >
      Add Now
    </button>
  </div>
)}
      <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: 32, marginBottom: 24 }}>
        <h3 style={{ marginBottom: 20, fontSize: '1rem' }}>Personal Information</h3>

        {!otpStep ? (
          // ── Normal profile form ──
          <form onSubmit={handleProfile}>
            <div className="profile-form-grid">
              {[['name', 'Full Name', 'text'], ['phone', 'Phone', 'tel']].map(([n, l, t]) => (
                <div className="form-group" key={n}>
                  <label>{l}</label>
                  <input
                    className="form-control"
                    type={t}
                    value={form[n]}
                    onChange={e => setForm(f => ({ ...f, [n]: e.target.value }))}
                    placeholder={l}
                  />
                </div>
              ))}
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label>Address</label>
                <textarea
                  className="form-control"
                  value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  rows={2}
                  placeholder="Your address"
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : phoneChanged ? 'Save & Verify Phone' : 'Save Changes'}
            </button>
          </form>
        ) : (
          // ── OTP verification step ──
          <div>
            <p style={{ marginBottom: 16, color: 'var(--text-muted)' }}>
              An OTP has been sent to <strong>{form.phone}</strong> via WhatsApp. Enter it below to verify.
            </p>
            <div className="form-group" style={{ maxWidth: 240 }}>
              <label>Enter OTP</label>
              <input
                className="form-control"
                type="text"
                maxLength={6}
                value={otp}
                onChange={e => setOtp(e.target.value)}
                placeholder="6-digit OTP"
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleOtpVerify} disabled={saving}>
                {saving ? 'Verifying...' : 'Verify & Save'}
              </button>
              <button className="btn btn-outline" onClick={() => { setOtpStep(false); setOtp(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {/* rest of the profile page stays the same... */}
    </div>
  );
};
// ── Dashboard Shell ──────────────────────────────────────────────────────────
const DashboardPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  

  const navItems = [
  { path: '/dashboard/profile', label: 'Profile', icon: <FiUser size={17}/> },
    { path: '/dashboard', label: 'My Orders', icon: <FiPackage size={17}/> },
  
  { path: '/dashboard/business', label: 'My Business', icon: <FiBriefcase size={17}/> },

];

  return (
    <div className="dashboard-wrapper">
      <div className="container dashboard-container">
        <div className="dashboard-grid">
          {/* Sidebar */}
          <div className="dashboard-sidebar">
            <div className="dashboard-sidebar-user">
              <div className="dashboard-sidebar-avatar">
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{fontWeight:700,fontSize:'0.9rem'}}>{user?.name}</div>
                <div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>{user?.role}</div>
              </div>
            </div>
            {navItems.map(item => (
              <Link key={item.path} to={item.path}
                style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:8,fontSize:'0.88rem',fontWeight:500,marginBottom:2,
                  background:location.pathname===item.path?'rgba(232,64,64,0.08)':'transparent',
                  color:location.pathname===item.path?'var(--primary)':'var(--text-muted)'}}>
                {item.icon}{item.label}
              </Link>
            ))}
            {(user?.role === 'admin' || user?.role === 'staff') && (
              <Link to="/admin" style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',borderRadius:8,fontSize:'0.88rem',fontWeight:500,color:'#7c3aed',marginBottom:2}}>
                <FiSettings size={17}/> Admin Panel
              </Link>
            )}
            
          </div>
          {/* Content */}
          <div>
            <Routes>
              <Route index element={<OrdersList/>}/>
              <Route path="profile" element={<Profile/>}/>
              <Route path="business" element={<MyBusiness/>}/>
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;